import { reactive } from 'vue';
import { supabase } from '../supabase.js';
import { coordinateUtils } from '../utils/coordinateUtils.js';
import { useAuth } from './useAuth.js';

export const useNetworkStore = () => {

    const auth = useAuth(); // [NEW] Access Auth Store

    // State
    const state = reactive({
        isLoading: false,
        error: null
    });

    // Actions
    async function fetchShopSources(shopId) {
        if (!shopId) return [];




        const { data, error } = await supabase.from('shop_partnerships')
            .select(`id, status, embed_token, source_shop:shops!source_shop_id ( * )`)
            .eq('distributor_shop_id', shopId)
            .in('status', ['active', 'suspended']);

        if (error) {
            console.error("[NetworkStore] fetchShopSources Error:", error);
            throw error;
        }

        return (data || []).map(d => {
            const s = d.source_shop;
            if (!s) return null;
            const coords = coordinateUtils.parseCoordinates(s.coordinates);
            return { ...s, coordinates: coords, partnershipId: d.id, status: d.status, embedToken: d.embed_token };
        }).filter(Boolean);
    }

    async function fetchShopDistributors(shopId) {
        if (!shopId) return [];




        const { data, error } = await supabase.from('shop_partnerships')
            .select(`id, status, embed_token, distributor_shop:shops!distributor_shop_id ( * )`)
            .eq('source_shop_id', shopId)
            .in('status', ['active', 'suspended']);

        if (error) {
            console.error("[NetworkStore] fetchShopDistributors Error:", error);
            throw error;
        }

        return (data || []).map(d => {
            const s = d.distributor_shop;
            if (!s) return null;
            const coords = s.coordinates ? coordinateUtils.parseCoordinates(s.coordinates) : null;
            return { ...s, coordinates: coords, partnershipId: d.id, status: d.status, embedToken: d.embed_token };
        }).filter(Boolean);
    }

    async function fetchPendingPartnerships(userEmail, shopId) {
        console.log(`[NetworkStore] fetchPendingPartnerships called for ${userEmail}`);
        if (!userEmail) return [];




        const { data, error } = await supabase.from('partnership_requests')
            .select(`*, requester_shop:shops!requester_shop_id ( * )`)
            .eq('target_user_email', userEmail)
            .eq('status', 'pending');

        if (error) {
            console.error("[NetworkStore] Supabase Query Error:", error);
            return [];
        }

        // Filter out self-invites
        if (shopId && data) {
            return data.filter(r => r.requester_shop_id !== shopId);
        }

        return data || [];
    }

    async function fetchSentPartnershipRequests(shopId) {
        if (!shopId) return [];




        const { data, error } = await supabase.from('partnership_requests')
            .select('*')
            .eq('requester_shop_id', shopId)
            .eq('status', 'pending');

        if (error) throw error;
        return data || [];
    }

    async function cancelPartnershipRequest(requestId) {
        console.log("[NetworkStore] cancelPartnershipRequest called for ID:", requestId);

        // Soft delete for B2B integrity
        const { error, data } = await supabase
            .from('partnership_requests')
            .update({ status: 'rejected' })
            .eq('id', requestId)
            .select();

        if (error) {
            console.error("[NetworkStore] Error cancelling request:", error);
            throw error;
        }
        console.log("[NetworkStore] Request cancelled successfully. Data:", data);
    }

    async function updatePartnershipStatus(partnershipId, status) {
        if (!partnershipId) throw new Error("Invalid partnership ID");
        if (!['active', 'suspended'].includes(status)) throw new Error("Invalid status update");




        const { error } = await supabase.from('shop_partnerships')
            .update({ status: status })
            .eq('id', partnershipId);
        if (error) throw error;
    }

    async function terminatePartnership(partnershipId) {
        if (!partnershipId) throw new Error("Invalid partnership ID");




        // Soft Delete
        const { error } = await supabase.from('shop_partnerships')
            .update({ status: 'terminated' })
            .eq('id', partnershipId);
        if (error) throw error;
    }

    async function respondToPartnership(requestId, status, userShopId) {

        const session = auth.authState.session; // Just for checking, but interceptor handles validity


        if (status === 'active') {
            await supabase.rpc('accept_partnership_request', {
                request_id: requestId,
                user_shop_id: userShopId
            });
        } else {
            await supabase.from('partnership_requests').update({ status: 'rejected' }).eq('id', requestId);
        }
    }

    async function sendPartnershipInvite(targetEmail, type = 'Distributor', requesterShopId) {
        if (!requesterShopId) throw new Error("No requester shop ID provided");

        // Use Edge Function for Token-based flow
        const { data, error } = await supabase.functions.invoke('request-partnership', {
            body: {
                email: targetEmail,
                shop_id: requesterShopId,
                type: type,
                origin: window.location.origin
            }
        });

        if (error) {
            console.error("[NetworkStore] Request Partnership Error:", error);
            throw new Error(error.message || "Failed to send partnership request");
        }

        // Refresh sent requests locally
        await fetchSentPartnershipRequests(requesterShopId);
        return data;
    }

    async function acceptPartnershipByToken(token, userShopId) {
        if (!token || !userShopId) throw new Error("Missing token or shop ID");

        const { data, error } = await supabase.functions.invoke('accept-partnership-invite', {
            body: {
                token,
                user_shop_id: userShopId
            }
        });

        if (error) {
            console.error("[NetworkStore] Accept Partnership Error:", error);
            throw new Error(error.message || "Failed to accept partnership");
        }

        return data; // { success: true, request }
    }

    async function fetchPartnerSites(shopId) {
        if (!shopId) return [];




        const { data, error } = await supabase
            .from('sites')
            .select('*')
            .eq('shop_id', shopId);

        if (error) {
            console.error("[NetworkStore] Fetch Partner Sites Error:", error);
            return [];
        }

        return data.map(s => ({
            id: s.id,
            shopId: s.shop_id,
            name: s.name,
            coordinates: (typeof s.lat === 'number' && typeof s.lng === 'number')
                ? [s.lng, s.lat]
                : coordinateUtils.parseCoordinates(s.coordinates || s.location),
            description: s.description,
            status: s.status,
            location: '',
            depth: s.depth_range || '10-20m',
            difficulty: Array.isArray(s.difficulty) ? s.difficulty : [],
            structure: Array.isArray(s.structure) ? s.structure : [],
            sailTime: s.sail_time || '',
            entryType: Array.isArray(s.entry_type) ? s.entry_type : [],
            media: {
                images: s.photos || [],
                video: s.hero_video || ''
            }
        }));
    }

    return {
        // State
        state,

        // Actions
        fetchPartnerSites,
        fetchShopSources,
        fetchShopDistributors,
        fetchPendingPartnerships,
        fetchSentPartnershipRequests,
        cancelPartnershipRequest,
        updatePartnershipStatus,
        terminatePartnership,
        respondToPartnership,
        sendPartnershipInvite,
        acceptPartnershipByToken
    };
};
